import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ButtonComponent } from 'src/app/shared/components/buttons/button/button.component';
import { MatChip } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { Luxon } from 'src/app/helpers/luxon';
import { entradaDoCatalogo, resumoDeProgresso } from './jornada.catalogo';
import { EntradaDoCatalogo, MissaoResposta } from './jornada.model';

const TAMANHO_MINIMO_DA_JUSTIFICATIVA = 10;
const TAMANHO_MAXIMO_DA_JUSTIFICATIVA = 500;

/**
 * As missões de uma fase: o bloco "sua próxima melhor ação" e um acordeão
 * (`mat-expansion-panel`, Lei 1) por missão. Estado é cor + forma + texto —
 * legível sem movimento (spec §6.6).
 *
 * `podeVer` chega do host como função: é ele quem conhece o store de
 * permissões, e a lista continua montável sem sessão nenhuma.
 */
@Component({
  selector: 'mg-lista-de-missoes',
  templateUrl: './lista-de-missoes.component.html',
  styleUrls: ['./lista-de-missoes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatExpansionModule, ButtonComponent, MatChip, MatIcon],
})
export class ListaDeMissoesComponent implements OnChanges {
  @Input({ required: true }) missoes: MissaoResposta[] = [];
  @Input() proxima: string | null = null;
  @Input() mostrarProxima = true;
  @Input() idsNovos: string[] = [];
  @Input() podeVer: (permissao: string) => boolean = () => false;
  /**
   * CV-CLI-31 — id da missão cuja escrita de exceção ("não se aplica" /
   * "voltar a considerar") está em voo. Quem faz a chamada é o
   * `JornadaService`, não esta lista: sem o id vindo de fora não haveria onde
   * girar o spinner do próprio botão.
   */
  @Input() excecaoEmAndamento: string | null = null;

  @Output() readonly irPara = new EventEmitter<string>();
  @Output() readonly focar = new EventEmitter<string>();
  @Output() readonly naoSeAplica = new EventEmitter<{ id: string; justificativa: string }>();
  @Output() readonly removerExcecao = new EventEmitter<string>();

  protected readonly minimo = TAMANHO_MINIMO_DA_JUSTIFICATIVA;
  protected readonly maximo = TAMANHO_MAXIMO_DA_JUSTIFICATIVA;

  /**
   * O que o usuário digitou em cada "não se aplica" ainda não enviado. Só se
   * apaga quando `missoes` troca — outra fase, ou a recarga depois de a API
   * aceitar; em erro (403 e afins) o texto fica para a nova tentativa.
   */
  private readonly justificativas = new Map<string, string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['missoes']) {
      this.justificativas.clear();
    }
  }

  protected entrada(id: string): EntradaDoCatalogo {
    return entradaDoCatalogo(id);
  }

  /** A próxima melhor ação, só quando ela está ENTRE as missões desta fase. */
  protected get proximaMissao(): MissaoResposta | null {
    return this.missoes.find(m => m.id === this.proxima) ?? null;
  }

  protected classes(missao: MissaoResposta): string {
    const estado = missao.estado.replace(/_/g, '-');
    const nova = this.idsNovos.includes(missao.id) ? ' mg-missao--nova' : '';

    return `mg-missao mg-missao--${estado}${nova}`;
  }

  protected resumo(missao: MissaoResposta): string {
    switch (missao.estado) {
      case 'em_breve':
        return 'Em breve';
      case 'nao_se_aplica':
        return 'Não se aplica';
      default:
        return resumoDeProgresso(this.entrada(missao.id), missao.numerador, missao.denominador, missao.meta, missao.estado);
    }
  }

  protected estadoResumido(missao: MissaoResposta): string {
    switch (missao.estado) {
      case 'concluida':
        return 'Concluída';
      case 'em_andamento':
        return 'Em andamento';
      case 'atencao':
        return 'Atenção';
      case 'em_breve':
        return 'Em breve';
      case 'nao_se_aplica':
        return 'Não se aplica';
      default:
        return 'A iniciar';
    }
  }

  /** `atencao` já bateu o marco: o XP é dela, mesmo com o indicador abaixo da meta agora. */
  protected xp(missao: MissaoResposta): string {
    return missao.estado === 'concluida' || missao.estado === 'atencao' ? '✓ 100 XP' : '+100 XP';
  }

  protected temAcoes(missao: MissaoResposta): boolean {
    return missao.estado !== 'em_breve' && missao.estado !== 'nao_se_aplica';
  }

  protected podeExcetuar(missao: MissaoResposta): boolean {
    return this.temAcoes(missao) && missao.estado !== 'concluida';
  }

  protected podeFocar(missao: MissaoResposta): boolean {
    return missao.disponivel && this.podeExcetuar(missao) && this.podeVer(this.entrada(missao.id).permissao);
  }

  protected dataDaExcecao(iso: string | null): string {
    return iso ? Luxon.from(iso).toFormat('dd/MM/yyyy') : '';
  }

  protected aoDigitar(id: string, evento: Event): void {
    this.justificativas.set(id, (evento.target as HTMLTextAreaElement).value);
  }

  protected justificativaValida(id: string): boolean {
    const tamanho = (this.justificativas.get(id) ?? '').trim().length;

    return tamanho >= TAMANHO_MINIMO_DA_JUSTIFICATIVA && tamanho <= TAMANHO_MAXIMO_DA_JUSTIFICATIVA;
  }

  protected emitirNaoSeAplica(id: string): void {
    if (!this.justificativaValida(id)) {
      return;
    }

    this.naoSeAplica.emit({ id, justificativa: (this.justificativas.get(id) ?? '').trim() });
  }
}
